import React, { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import Select from 'react-select';
import { Row, Col, Form, OverlayTrigger, Tooltip } from 'react-bootstrap';
import axios from 'axios';
import debounce from 'lodash/debounce';
import DatePicker from 'react-datepicker';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'react-datepicker/dist/react-datepicker.css';
import config from '../../config';
import { v4 as uuidv4 } from 'uuid';
import { alertconfirmation } from '../../alerts/Alerts';



const PayScheduleFrequency = forwardRef(({ caseId, renewalCase, isDisableForUsers, initialFormValues = {}, onError = () => { } }, ref) => {
    const [schedules, setSchedules] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [validationErrors, setValidationErrors] = useState({});
    const options = [
        { value: 'weekly', label: 'Weekly' },
        { value: 'biweekly', label: 'Bi-Weekly' },
        { value: 'monthly', label: 'Monthly' },
        { value: 'semimonthly', label: 'Semi-Monthly' },
        { value: 'other', label: 'Other' }
    ];
const onlyHra = renewalCase?.products === 'HRA'


    useEffect(() => {
        const fetchSchedules = async () => {
            setLoading(true); // Ensure loading is true before fetching

            try {
                const response = await axios.get(`${config.API_URL}/PayScheduleFrequencies/ByCaseId/${caseId}`);
                const fetchedSchedules = response.data || [];

                // Set schedules first
                setSchedules(fetchedSchedules);

                // if (fetchedSchedules.length === 0) {

                //     handleAddPlan();
                // }
            } catch (error) {
                if (error.response?.status !== 404) {
                    setError('Error fetching case files.');
                }
                onError(error);
            } finally {
                setLoading(false);
            }
        };

        fetchSchedules();
    }, [caseId]); // Ensure `caseId` is the only dependency

    const debouncedUpdatePayScheduleOnlyHRA = useCallback(
        debounce(async (id) => {
            
            const updatedFields= {'scheduleFrequency': 'other', 'payDate': renewalCase?.renewalDate }
           
            try {
                const patchDocument = Object.keys(updatedFields).map(key => ({
                    path: `/${key}`,
                    value: updatedFields[key]
                }));
                await axios.patch(`${config.API_URL}/PayScheduleFrequencies/${id}`, patchDocument);
                setSchedules(prevSchedules =>
                    prevSchedules.map(schedule =>
                        schedule.id === id
                            ? { ...schedule, scheduleFrequency: 'other', 'payDate': renewalCase?.renewalDate }
                            : schedule
                    )
                );
                
            } catch (error) {
                console.error('Error updating plan:', error);
                onError(error);
            }

        }, 500),
        [onError, validationErrors, caseId]
    );


    const debouncedUpdate = useCallback(
        debounce(async (fieldName, value, id) => {

            try {
                const patchDocument = [
                    { op: 'replace', path: `/${fieldName}`, value: value }
                ];
                const url = `${config.API_URL}/PayScheduleFrequencies/${id}`;
                await axios.patch(url, patchDocument);
            } catch (error) {
                console.error('Error updating field:', error);
                onError(error);
            }

        }, 500),
        [onError]
    );

    const validateField = (name, value, index, updateState = true) => {
        let errors = { ...validationErrors };
        let fieldError = null;

        switch (name) {
            case 'scheduleFrequency':
                if (!value) {
                    fieldError = { [name]: 'Pay schedule frequency is required.' };
                } else {

                    if (errors[index]) delete errors[index][name];
                }
                break;

            case 'otherScheduleFrequency':
                if (schedules[index]?.otherScheduleFrequency === 'other' && !value) {
                    fieldError = { [name]: 'Other Pay schedule frequency is required.' };
                } else {

                    if (errors[index]) delete errors[index][name];
                }
                break;

            case 'payDate':
                if (!value) {
                    fieldError = { [name]: 'Pay date is required.' };
                } else {

                    if (errors[index]) delete errors[index][name];
                }
                break;

            default:
                break;
        }

        if (updateState && fieldError) {
            errors[index] = {
                ...errors[index],
                ...fieldError
            };
            setValidationErrors(errors);
        }

        return fieldError;
    };


    const validateAll = () => {

        let allValid = true;
        let updatedErrors = { ...validationErrors };

        schedules.forEach((schedule, index) => {

            const requiredFields = [
                'scheduleFrequency',
                'otherScheduleFrequency',
                'payDate'
            ];

            requiredFields.forEach(field => {
                const fieldError = validateField(field, schedule[field], index, false);
                if (fieldError) {
                    allValid = false;
                    updatedErrors = {
                        ...updatedErrors,
                        [index]: {
                            ...updatedErrors[index],
                            ...fieldError
                        }
                    };
                }
            });
        });

        setValidationErrors(updatedErrors);
        return allValid;
    };

    useImperativeHandle(ref, () => ({
        validateAll,
    }));

    const handleInputChange = (index, e) => {
        const { name, value } = e.target;
        validateField(name, value, index);

        const updatedSchedules = [...schedules];
        updatedSchedules[index] = { ...updatedSchedules[index], [name]: value };
        setSchedules(updatedSchedules);

        debouncedUpdate(name, value, updatedSchedules[index].id);
    };

    const handleSelectChange = (index, name, selectedOption) => {
        const value = selectedOption ? selectedOption.value : '';
        validateField(name, value, index);

        const updatedSchedules = [...schedules];
        updatedSchedules[index] = { ...updatedSchedules[index], [name]: value };
        setSchedules(updatedSchedules);

        debouncedUpdate(name, value, updatedSchedules[index].id);
    };

    const handleDateChange = (index, fieldName, date) => {
        if (date) {
            // Normalize the date to prevent timezone issues
            const localDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    
            // Format the date as MM/dd/yyyy
            const formattedDate = localDate.toLocaleDateString('en-US', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit'
            });
    
            // Perform field validation
            validateField(fieldName, formattedDate, index);
    
            // Update schedules without overwriting previous updates
            setSchedules((prevSchedules) => {
                const updatedSchedules = [...prevSchedules];
                updatedSchedules[index] = { ...updatedSchedules[index], [fieldName]: formattedDate };
    
                // Trigger the debounced update after state change
                debouncedUpdate(fieldName, formattedDate, updatedSchedules[index].id);
    
                return updatedSchedules;
            });
        } else {
            // Handle case when the date is cleared (null)
            setSchedules((prevSchedules) => {
                const updatedSchedules = [...prevSchedules];
                updatedSchedules[index] = { ...updatedSchedules[index], [fieldName]: '' }; // Clear the date
    
                // Trigger the debounced update with an empty value
                debouncedUpdate(fieldName, '', updatedSchedules[index].id);
    
                return updatedSchedules;
            });
        }
    };
    
    



    const customStyles = (hasError) => ({
        control: (provided) => ({
            ...provided,
            borderColor: hasError ? '#dc3545' : provided.borderColor,
            '&:hover': {
                borderColor: hasError ? '#dc3545' : provided.borderColor,
            },
            boxShadow: hasError ? '0 0 10px rgba(220, 53, 69, 0.5)' : provided.boxShadow,
        }),
    });


    const handleDeletePlan = async (index) => {
        alertconfirmation(
            `Do you want to delete this specific  Pay schedule frequency ?`,
            "Confirmation",
            async () => {
                try {
                    const id = schedules[index].id;
                    await axios.delete(`${config.API_URL}/PayScheduleFrequencies/${id}`);
                    const updatedSchedules = schedules.filter((_, i) => i !== index);
                    setSchedules(updatedSchedules);
                } catch (error) {
                    console.error('Error deleting plan:', error);
                    onError(error);
                }
            }
        );
    };

    const handleAddPlan = async () => {
        try {


            const newPlan = { ...initialFormValues, caseId: caseId };

            const response = await axios.post(`${config.API_URL}/PayScheduleFrequencies`, newPlan);

            const addedSchedule = response.data;
            console.error(addedSchedule);
            setSchedules([...schedules, addedSchedule]);

        } catch (error) {
            console.error('Error adding new plan:', error);
            onError(error);
        }
    };


const alreadyUpdatedRef = useRef(new Set());

useEffect(() => {
  if (!onlyHra || !Array.isArray(schedules)) return;

  schedules.forEach(sc => {
    const id = sc?.id;
    const freq = (sc?.scheduleFrequency ?? '').toLowerCase();

    
    if (id && !sc?.scheduleFrequency && !alreadyUpdatedRef.current.has(id)) {
      debouncedUpdatePayScheduleOnlyHRA(id);
      alreadyUpdatedRef.current.add(id);
    }

  });
}, [onlyHra, schedules, debouncedUpdatePayScheduleOnlyHRA]);


    //const isDisabled = renewalCase?.ebSubmissionStatus === 'Submitted' || isDisableForUsers;
    const isDisabled =  isDisableForUsers;


    return (

        <>
            <h5 id="payScheduleTitle" className="card-title">

                Pay Schedule Frequency
            </h5>
            
            {schedules.map((schedule, index) => (
                
                <div key={index}>
                
                    <Row className="mb-2 align-items-center">
                        <Col lg={3}>

                            <Select
                                className={`flex-grow-1 `}
                                title="scheduleFrequency"
                                name="scheduleFrequency"
                                options={options}
                                value={options?.find(option => option.value === schedule.scheduleFrequency)}
                                placeholder="Pay Schedule Frequency"
                                onChange={(selectedOption) => handleSelectChange(index, 'scheduleFrequency', selectedOption)}
                                styles={customStyles(validationErrors[index] && validationErrors[index].scheduleFrequency)}
                                isDisabled={isDisabled}
                            />

                        </Col>
                        <Col lg={3}>
                            <OverlayTrigger
                                placement="top"
                                overlay={<Tooltip id="payDate-tooltip">First Paycheck Date in New Plan Year</Tooltip>}
                            >
                                <div>


                                    <DatePicker
                                        className={`form-control ${validationErrors[index] && validationErrors[index].payDate ? 'is-invalid' : ''}`}
                                        selected={schedule.payDate || ''}
                                        onChange={(date) => handleDateChange(index, 'payDate', date)}
                                        placeholderText="First Paycheck Date in New Plan Year"
                                        dateFormat="MM/dd/yyyy"
                                        minDate={new Date()}
                                        disabled={isDisabled}
                                    />
                                </div>
                            </OverlayTrigger>
                        </Col>
                        <Col lg={2} className="d-flex justify-content-between">
                            {schedules.length > 1 && (<button
                                className="delete-plan-info"
                                onClick={() => handleDeletePlan(index)} disabled={isDisabled}
                            >
                                <span className="fa fa-trash-alt me-1" id="delete"></span>
                            </button>)}




                            <button type="button"
                                className="add-coverage ms-4"
                                onClick={handleAddPlan}
                                disabled={isDisabled}
                            >
                                <i className="fa fa-plus"></i>
                            </button>

                        </Col>
                    </Row>
                    {schedule?.scheduleFrequency === 'other' && (
                        <Row className="mb-2 align-items-center">
                            <Col xs={4}>
                                <Form.Control
                                    type="text"
                                    placeholder="Other Schedule Frequency"
                                    name='otherScheduleFrequency'
                                    value={schedule?.otherScheduleFrequency || ''}
                                    onChange={(e) => handleInputChange(index, e)}
                                    disabled={isDisabled}
                                />

                            </Col>
                        </Row>
                    )}
                </div>
            ))}

        </>
    );
});

export default PayScheduleFrequency;


